import { ERROR_CODES, type ErrorCode, type FieldError } from '@kraaapf/contracts';

/**
 * Ein Fehler des Masters, wie ihn die Oberfläche anzeigen kann.
 *
 * ## Warum die Meldung hier entsteht und nicht im Master
 *
 * Der Master liefert zu jedem Fehler bereits einen deutschen Satz — und der
 * ist gut, weil er den konkreten Fall kennt („Node ‚worker-01' wird bereits
 * entleert"). Wofür er **nicht** gemacht ist, ist die Bedienung: Er sagt, was
 * schiefging, nicht, was jetzt zu tun ist.
 *
 * Deshalb beides: `message` ist die Auskunft des Masters, `hint` die
 * Handlungsanweisung aus dem stabilen Code. Die Oberfläche zeigt die eine über
 * der anderen. Bei einem unbekannten Code bleibt `hint` leer — eine erfundene
 * Handlungsanweisung wäre schlechter als keine.
 */
export class PortalClientError extends Error {
  public readonly code: ErrorCode | 'NETWORK_ERROR' | 'CONTRACT_MISMATCH';
  public readonly status: number;
  /** Was der Benutzer tun kann, oder `null`. */
  public readonly hint: string | null;
  /** Nur bei `VALIDATION_FAILED`: die Felder, die beanstandet wurden. */
  public readonly fieldErrors: readonly FieldError[];
  /** Für die Rückfrage beim Betreiber — sie steht in jedem Log des Masters. */
  public readonly correlationId: string | null;

  public constructor(input: {
    code: ErrorCode | 'NETWORK_ERROR' | 'CONTRACT_MISMATCH';
    message: string;
    status: number;
    fieldErrors?: readonly FieldError[];
    correlationId?: string | null;
  }) {
    super(input.message);

    this.name = 'PortalClientError';
    this.code = input.code;
    this.status = input.status;
    this.hint = HINTS[input.code] ?? null;
    this.fieldErrors = input.fieldErrors ?? [];
    this.correlationId = input.correlationId ?? null;
  }

  /** Ist das ein Fehler, den ein erneuter Versuch beheben könnte? */
  public get isRetryable(): boolean {
    return (
      this.code === 'NETWORK_ERROR' ||
      this.code === ERROR_CODES.upstreamUnavailable ||
      this.code === ERROR_CODES.rateLimited
    );
  }
}

/**
 * Was der Betreiber tun kann — je stabilem Fehlercode.
 *
 * Bewusst nicht für jeden Code ein Eintrag: Wo es nichts zu tun gibt, steht
 * nichts. Eine Zeile wie „Bitte versuchen Sie es erneut" unter einer Meldung,
 * die einen Zustandskonflikt beschreibt, ist Füllmaterial und macht die
 * nützlichen Hinweise unglaubwürdig.
 */
const HINTS: Partial<Record<string, string>> = {
  [ERROR_CODES.unauthenticated]: 'Die Sitzung ist abgelaufen. Bitte neu anmelden.',
  [ERROR_CODES.forbidden]:
    'Dieses Konto hat keinen Zugang zu diesem Bereich. Das ist keine Störung, sondern eine Grenze.',
  [ERROR_CODES.rateLimited]:
    'Zu viele Anfragen in kurzer Folge. Einen Moment warten und erneut versuchen.',
  [ERROR_CODES.noSchedulableNode]:
    'Kein Server kann diese Website aufnehmen. Entweder ist die Flotte voll, oder alle Nodes sind aus der Platzierung genommen — die Flottenansicht zeigt, welches von beidem.',
  [ERROR_CODES.operationConflict]:
    'Für diese Website läuft bereits ein Vorgang. Die Operationsansicht zeigt, welcher; danach ist der Weg wieder frei.',
  [ERROR_CODES.dnsVerificationFailed]:
    'Die Domain zeigt noch nicht auf die Edge. Der A-Eintrag steht in der Anleitung; nach einer Änderung dauert es je nach Anbieter einige Minuten.',
  [ERROR_CODES.domainAlreadyClaimed]:
    'Diese Domain gehört bereits zu einer anderen Website. Sie muss dort zuerst entfernt werden.',
  [ERROR_CODES.domainNotVerified]:
    'Die Domain ist eingetragen, aber noch nicht bestätigt. Sie wird erst nach der DNS-Prüfung ausgeliefert.',
  [ERROR_CODES.domainReserved]:
    'Dieser Name gehört der Plattform selbst und lässt sich nicht vergeben.',
  [ERROR_CODES.storageBoxUnavailable]:
    'Keine Storage Box hat Platz für das Repository dieser Website. Die Website läuft trotzdem — sie wird nur nicht gesichert, bis eine Box Platz hat.',
  [ERROR_CODES.backupNotConfigured]:
    'Für diese Website ist keine Sicherung eingerichtet. Ohne Repository gibt es nichts, wohin zurückzukehren wäre.',
  [ERROR_CODES.snapshotUnknown]:
    'Diese Momentaufnahme steht nicht im Repository dieser Website. Die Liste zeigt, welche es gibt.',
  [ERROR_CODES.databaseLimitReached]:
    'Die Website hat die erlaubte Anzahl an Datenbanken erreicht. Eine bestehende entfernen oder eine andere Website wählen.',
  [ERROR_CODES.sftpLimitReached]: 'Die Website hat die erlaubte Anzahl an SFTP-Konten erreicht.',
  [ERROR_CODES.siteSpecRejected]:
    'Der Agent hat die Spezifikation abgelehnt. Ein erneuter Versuch ändert daran nichts — das Log des Workers nennt den Grund.',
  [ERROR_CODES.upstreamUnavailable]:
    'Ein nachgelagerter Dienst antwortet nicht. Ein erneuter Versuch in einer Minute ist der richtige Schritt.',
  [ERROR_CODES.artifactNotFound]:
    'Das Agent-Artefakt liegt auf dem Master nicht bereit. Es wird über scripts/release-agent.sh gebaut und übertragen.',
  NETWORK_ERROR:
    'Der Master ist gerade nicht erreichbar. Die Websites laufen davon unberührt weiter — der Master steht nicht im Datenpfad.',
  CONTRACT_MISMATCH:
    'Die Antwort des Masters passt nicht zu dieser Oberfläche. Vermutlich läuft eine ältere oder neuere Fassung als erwartet.',
};
